import React, { useEffect, useState} from 'react'
import axios from 'axios'
import { Table, Form, Button, Modal } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import { BiTask } from "react-icons/bi";
import { MdExitToApp } from "react-icons/md";
import { BASE_URL } from '../../utils/constants';
import { Tooltip } from 'react-tooltip'
import { useNavigate, Link } from 'react-router-dom';
import { MdOutlinePhoneIphone } from "react-icons/md";
import { GoMail } from "react-icons/go";
import moment from 'moment'
import { toast } from 'react-toastify';
import CircleProgressBar from '../../components/CircleProgressBar/CircleProgressBar';
import Pagination from '../../components/Pagination/Pagination'; // Import the new Pagination component

import './Dashboard.scss'

const Dashboard = ({ user, logUserOut }) => {
  const [loading, setLoading] = useState(true)
  const [timeInterval, setTimeInterval] = useState(14)
  const [pendingTasks, setPendingTasks] = useState([])
  const [patientDetails, setPatientDetails] = useState({})
  const [surveyUrl, setSurveyUrl] = useState("")
  const [patientCompliancePercentage , setPatientCompliancePercentage] = useState(0.00)
  const [complicationPercentage , setComplicationPercentage] = useState(0.00)
  const [surgeonTasks, setSurgeonTasks] = useState(0)
  const navigate = useNavigate();
  // New state variables for UI-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Or the number of items you want per page
  // Mobile modal state
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const getPatientPendingTasksWithinTimeInterval = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    }
    try {
      // The API now returns all tasks for the given time interval
      const response = await axios.get(`${BASE_URL}/api/v1/pending-task/within-time-interval/${timeInterval}`, config)
      
      setPendingTasks(response.data.pendingTasks.map((pendingTask, index) => {
        let milestone = ""
        let amount = ""
        if(pendingTask.current_milestone.split(" ").length > 1) {
          [amount, milestone] = pendingTask.current_milestone.split(" ")
        }
        else {
          milestone = pendingTask.current_milestone
        }
        const study = pendingTask.study === "SAS"? "shoulder-arthroscopy" : pendingTask.study === "SAP"? "shoulder-arthroplasty" : 'elbow'
        return {
          ...pendingTask,
          surveyUrl: `/patient/${pendingTask.custom_patient_id}/event/${pendingTask.event_id}/${study}/${pendingTask.treatment_side.toLowerCase()}/self-assessment/${milestone}${milestone !== "pre-treatment"? `/${amount}` : ""}`
        }
      }))
      setLoading(false)
    }
    catch (error) {
      console.log(error)
      const message = error.response && error.response.data.message ? error.response.data.message : error.message
      if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
        logUserOut()
      }
    }
  }
  
  useEffect(() => {
    // The data is only fetched when the time interval changes
    getPatientPendingTasksWithinTimeInterval()
  }, [timeInterval])

  useEffect(() => {
    const getCompliancePercentages = async () => {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
      try {
        const response = await axios.get(`${BASE_URL}/api/v1/patient/patient-compliance-percentages`, config)
        setPatientCompliancePercentage(response.data.patientCompliancePercentage)
        setComplicationPercentage(response.data.complicationPercentage)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getCompliancePercentages()
  }, [])

  useEffect(() => {
    const getOpenSurgeonTaskCount = async () => {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
      try {
        const response = await axios.get(`${BASE_URL}/api/v1/surgeon/open-task-count`, config)
        setSurgeonTasks(response.data.openSurgeonTaskCount)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getOpenSurgeonTaskCount()
  }, [])

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [])

  const handleStatusClick = (pendingTask) => {
    setPatientDetails({
      customPatientId: pendingTask.custom_patient_id,
      eventId: pendingTask.event_id,
      milestone: pendingTask.current_milestone,
      email: pendingTask.email,
      cellPhoneNumber: pendingTask.cell_phone_number,
      numberOfRemindersSent: pendingTask.number_of_reminders_sent,
      lastReminderSent: pendingTask.last_reminder_sent_timestamp,
      surveyUrl: pendingTask.surveyUrl
    });
    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  const sendReminder = async () => {
    const config = {
      headers: {
        Authorization: `Bearer ${user.token}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
    }
    const body = {
      milestone: patientDetails.milestone,
      email: patientDetails.email,
      surveyUrl: patientDetails.surveyUrl,
      eventId: patientDetails.eventId,
      customPatientId: patientDetails.customPatientId,
    }
    try {
      const response = await axios.post(`${BASE_URL}/api/v1/pending-task/send-reminder`, body, config)
      toast.success('Reminder sent!', {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "light",
      });
      if (isMobile) {
        setShowMobileModal(false);
      }
    }
    catch(error) {
      console.log()
      toast.error('Failed to send reminder!', {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "light",
      });
    }

  }

  // Calculate the tasks for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTasks = pendingTasks.slice(startIndex, endIndex);

  return (
    <div className="dashboard">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <div className="dashboard-header">
            <div>
              <h2 className="logo"><BiTask /> Pending Patient Tasks</h2> 
              <div className="compliance-scores">
                <div className="progress-bars-group">
                  <div className='progress-bar'>
                    <CircleProgressBar percentage={patientCompliancePercentage} colour="rgb(38, 197, 102)" width="100px" height="100px" radius="35" onClickAction={() => navigate(`/patient-compliance`)}/>
                    <p>Compliance</p>
                  </div>
                  <div className='progress-bar'>
                    <CircleProgressBar percentage={complicationPercentage} colour="rgb(38, 197, 102)" width="100px" height="100px" radius="35" onClickAction={() => navigate(`/patient-complications`)}/>
                    <p>Complications</p>
                  </div>
                  <div className='progress-bar'>
                    <CircleProgressBar percentage={surgeonTasks} number={true} colour={surgeonTasks > 0 ? "rgb(184, 50, 38)" : "rgb(38, 197, 102)"} width="100px" height="100px" radius="35" onClickAction={() => navigate(`/surgeon-tasks`)}/>
                    <p>Surgeon Tasks</p>
                  </div>
                </div>
                <Form.Group className="form-group time-interval">
                  <Form.Label>Tasks due within</Form.Label>
                  <Form.Select
                    value={timeInterval}
                    onChange={(event) => setTimeInterval(event.target.value)}>
                    <option value="7">1 week</option>
                    <option value="14">2 weeks</option>
                    <option value="30">1 month</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </div>
          <div className='dashboard-table'>
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='when'>When</th>
                  <th className='deadline'>Deadline</th>
                  <th className='custom-id'>Custom Patient ID</th>
                  <th className='study'>Study</th>
                  <th className='event-date'>Procedure Date</th>
                  <th className='milestone'>Milestone</th>
                  <th className='status'>Status</th>
                  <th className='button'></th>
                </tr>
              </thead>
              <tbody>
                {currentTasks.map((pendingTask, index) => { // Use the sliced array here
                  return (
                    <tr key={index}>
                      <td className="when">
                        {
                        pendingTask.days_until_survey_deadline === 0 ? "Due Today" : 
                        pendingTask.days_until_survey_deadline > 0 ? `Upcoming - ${pendingTask.days_until_survey_deadline} day${pendingTask.days_until_survey_deadline > 1? "s" : ""}` :
                        `Overdue - ${Math.abs(pendingTask.days_until_survey_deadline)} day${Math.abs(pendingTask.days_until_survey_deadline) > 1? "s" : ""}`
                        }
                      </td>
                    <td className="deadline">{moment(pendingTask.survey_deadline).format("DD/MM/YYYY")}</td>
                      <td className="custom-id">{pendingTask.custom_patient_id}</td>
                      <td className="study">{pendingTask.study}</td>
                      <td className="event-date">{moment(pendingTask.event_date).format("DD/MM/YYYY")}</td>
                      <td className='milestone'>{pendingTask.current_milestone}</td>
                      <td className='status'
                        onMouseOver={() => !isMobile && handleStatusClick(pendingTask)}
                        onClick={() => isMobile && handleStatusClick(pendingTask)}>
                        <span
                          className={pendingTask.days_until_survey_deadline < 0? "purple-dot" : pendingTask.days_until_survey_deadline <= 5? "red-dot" : "yellow-dot"}
                          data-tooltip-id={!isMobile ? "status-tooltip" : undefined}
                          data-tooltip-offset={15}>!</span>
                      </td>
                      <td className="button"><Button variant="primary" onClick={() => navigate(`/patients/${pendingTask.custom_patient_id}`)}>
                        <MdExitToApp />
                      </Button></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          {/* Add the new Pagination component here */}
          <Pagination
            currentPage={currentPage}
            totalItems={pendingTasks.length} // The total number of tasks is the length of the full array
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />

          {/* Survey Status Content - Shared between tooltip and modal */}
          {!isMobile && (
            <Tooltip id="status-tooltip" clickable className='status-tooltip' place='left'>
              <div className="survey-status-content">
                <div className="survey-status-header">
                  <div className="header-title">Survey Status</div>
                  <div className="patient-info-compact">
                    <span className="patient-id">{patientDetails.customPatientId}</span>
                    <span className="divider">•</span>
                    <span className="milestone-text">{patientDetails.milestone}</span>
                  </div>
                </div>

                <div className="survey-status-body">
                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <Link to={patientDetails.surveyUrl} target="_blank" rel="noopener noreferrer" className="button-link">
                      <Button variant="primary" size="sm">
                        <MdExitToApp className="button-icon" />
                        Go to survey
                      </Button>
                    </Link>
                    <Button variant="warning" size="sm" onClick={sendReminder}>
                      <GoMail className="button-icon" />
                      Send reminder
                    </Button>
                  </div>

                  {/* Reminder Info */}
                  <div className="reminder-info-section">
                    <div className="reminder-stats">
                      <strong>{patientDetails.numberOfRemindersSent}</strong> reminder{patientDetails.numberOfRemindersSent !== 1 ? 's' : ''} sent
                      {patientDetails.lastReminderSent && (
                        <div className="last-sent">Last sent {moment(patientDetails.lastReminderSent).format("DD/MM/YYYY")}</div>
                      )}
                    </div>
                  </div>

                  <hr className="content-divider" />

                  {/* Patient Contact Information */}
                  <div className="patient-contact-section">
                    <h6 className="section-title">Contact</h6>
                    <div className="contact-info">
                      <div className="contact-item">
                        <div className="icon-wrapper email-icon">
                          <GoMail />
                        </div>
                        <div className="contact-details">
                          <div className="contact-label">Email</div>
                          <div className="contact-text">{patientDetails.email || 'Not provided'}</div>
                        </div>
                      </div>
                      <div className="contact-item">
                        <div className="icon-wrapper phone-icon">
                          <MdOutlinePhoneIphone />
                        </div>
                        <div className="contact-details">
                          <div className="contact-label">Phone</div>
                          <div className="contact-text">{patientDetails.cellPhoneNumber || 'Not provided'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>
          )}

          {/* Mobile Modal */}
          {isMobile && (
            <Modal show={showMobileModal} onHide={() => setShowMobileModal(false)} centered className="survey-status-modal">
              <Modal.Header closeButton>
                <Modal.Title>
                  <div className="modal-title-content">
                    <div>Survey Status</div>
                    <div className="patient-info-compact">
                      <span className="patient-id">{patientDetails.customPatientId}</span>
                      <span className="divider">•</span>
                      <span className="milestone-text">{patientDetails.milestone}</span>
                    </div>
                  </div>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="survey-status-content">
                  <div className="survey-status-body">
                    {/* Action Buttons */}
                    <div className="action-buttons">
                      <Link to={patientDetails.surveyUrl} target="_blank" rel="noopener noreferrer" className="button-link" onClick={() => setShowMobileModal(false)}>
                        <Button variant="primary" size="sm">
                          <MdExitToApp className="button-icon" />
                          Go to survey
                        </Button>
                      </Link>
                      <Button variant="warning" size="sm" onClick={sendReminder}>
                        <GoMail className="button-icon" />
                        Send reminder
                      </Button>
                    </div>

                    {/* Reminder Info */}
                    <div className="reminder-info-section">
                      <div className="reminder-stats">
                        <strong>{patientDetails.numberOfRemindersSent}</strong> reminder{patientDetails.numberOfRemindersSent !== 1 ? 's' : ''} sent
                        {patientDetails.lastReminderSent && (
                          <div className="last-sent">Last sent {moment(patientDetails.lastReminderSent).format("DD/MM/YYYY")}</div>
                        )}
                      </div>
                    </div>

                    <hr className="content-divider" />

                    {/* Patient Contact Information */}
                    <div className="patient-contact-section">
                      <h6 className="section-title">Contact</h6>
                      <div className="contact-info">
                        <div className="contact-item">
                          <div className="icon-wrapper email-icon">
                            <GoMail />
                          </div>
                          <div className="contact-details">
                            <div className="contact-label">Email</div>
                            <div className="contact-text">{patientDetails.email || 'Not provided'}</div>
                          </div>
                        </div>
                        <div className="contact-item">
                          <div className="icon-wrapper phone-icon">
                            <MdOutlinePhoneIphone />
                          </div>
                          <div className="contact-details">
                            <div className="contact-label">Phone</div>
                            <div className="contact-text">{patientDetails.cellPhoneNumber || 'Not provided'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          )}
        </>)}    
    </div>
  )
}

export default Dashboard